import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@roberttamas/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Search } from "@/components/icons";
import { RelativeTime } from "@/components/relative-time";
import i18n from "@/i18n";
import { listUsers } from "@/lib/api";
import type { ListUsersResp, UserListEntry } from "@/lib/types";

function providerLabel(id: string): string {
  switch (id) {
    case "google.com":
      return "Google";
    case "github.com":
      return "GitHub";
    case "password":
      return i18n.t("users.providerEmail");
    case "phone":
      return i18n.t("users.providerPhone");
    default:
      return id;
  }
}

export function UsersListPage() {
  const { t } = useTranslation();
  const query = useQuery<ListUsersResp>({ queryKey: ["users"], queryFn: listUsers });
  const [filter, setFilter] = useState("");

  const users = query.data?.users;
  const filtered = useMemo(() => {
    if (!users) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email ?? "").toLowerCase().includes(q) ||
        (u.displayName ?? "").toLowerCase().includes(q),
    );
  }, [users, filter]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("users.eyebrow")}
        title={t("users.title")}
        subtitle={t("users.subtitle")}
      />

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t("users.filterPlaceholder")}
          aria-label={t("users.filterAria")}
          className="pl-9"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("users.colUser")}</TableHead>
                <TableHead>{t("users.colTier")}</TableHead>
                <TableHead>{t("users.colVerified")}</TableHead>
                <TableHead>{t("users.colProvider")}</TableHead>
                <TableHead>{t("users.colCreated")}</TableHead>
                <TableHead>{t("users.colLastSignIn")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {query.isError && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="flex items-center justify-between gap-4 py-6 text-sm">
                      <span className="text-destructive">{t("users.loadFailed")}</span>
                      <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
                        {t("common.retry")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {query.data && query.data.users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="py-12 text-center text-sm text-muted-foreground">
                      {t("users.empty")}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {query.data && query.data.users.length > 0 && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="py-10 text-center text-sm text-muted-foreground">
                      {t("users.noMatch", { query: filter.trim() })}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {filtered.map((u: UserListEntry) => (
                <TableRow key={u.uid}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{u.email ?? u.displayName ?? u.uid}</span>
                      {u.displayName && u.email && (
                        <span className="text-xs font-normal text-muted-foreground">
                          {u.displayName}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.tier === "admin" ? "default" : "outline"}>
                      {u.tier === "admin" ? t("users.tierAdmin") : t("users.tierUser")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {u.emailVerified ? t("users.verifiedYes") : <span className="text-muted-foreground">{t("users.verifiedNo")}</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {u.providerIds.length ? u.providerIds.map(providerLabel).join(", ") : "-"}
                  </TableCell>
                  <TableCell>
                    <RelativeTime date={u.createdAt ? new Date(u.createdAt) : null} />
                  </TableCell>
                  <TableCell>
                    <RelativeTime date={u.lastSignInAt ? new Date(u.lastSignInAt) : null} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
